import React, { useState } from 'react';
import styled from 'styled-components';
import { CommonButton } from '../../../Styles/CommonStyles';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import nameStringFn from '../../../data/nameStringFn';
import { useUpdateComplaintsMutation } from '../../../features/complaints/complaintsApiSlice';

const FormContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  margin-top: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  width: 100%;
`;

const FormTitle = styled.h3`
  margin-bottom: 20px;
  color: #1976d2;
  font-size: 1.25rem;
`;

const FormRow = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  align-items: flex-start;
`;

const FormField = styled.div`
  flex: 1;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const Button = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
`;

const SubmitButton = styled(Button)`
  background-color: #d32f2f;
`;

const CancelButton = styled(Button)`
  background-color: #757575;
`;

const InfoBox = styled.div`
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const UserComplaintForm = ({ 
  row, 
  remark, 
  setRemark, 
  auth, 
  user_current_office, 
  getSingleuser, 
  supportusers,
  storeoffices,
  handleBack, 
  availableActions 
}) => {
  const [loading, setLoading] = useState(false);
  
  const [updateComplaint] = useUpdateComplaintsMutation();

  const escalationCount = row?.escalation_count || 0;
  const maxEscalations = 2;
  const remainingEscalations = maxEscalations - escalationCount;

  const closedAt = row?.closed_at ? new Date(row.closed_at) : null;
  const now = new Date();
  const fourDaysMs = 4 * 24 * 60 * 60 * 1000;
  const isWithinWindow = closedAt ? (now - closedAt) < fourDaysMs : false;
  const daysRemaining = closedAt ? Math.max(0, Math.ceil((fourDaysMs - (now - closedAt)) / (24 * 60 * 60 * 1000))) : 0;

  const canEscalate = row?.curr_status === 'Closed' && isWithinWindow && escalationCount < maxEscalations;

  const getL1SupportAssignee = () => {
    if (!storeoffices) return null;

    const storeOfficesArray = Object.values(storeoffices);
    const storeOffice = storeOfficesArray.find(x => x.storeoffice === row.store);
    const l1SupportUserId = storeOffice?.roleUsers?.L1Support;
    const l1SupportUser = l1SupportUserId && getSingleuser ? getSingleuser[l1SupportUserId] : null;

    if (!l1SupportUser || !l1SupportUserId) {
      console.error(`L1Support not found for store: ${row.store}`);
      return null;
    }

    return {
      id: l1SupportUserId,
      name: l1SupportUser.name || {},
      designation: l1SupportUser?.designation?.[row.store] || 'L1Support'
    };
  };

  const handleEscalate = async () => {
    if (!remark.trim()) {
      alert('Please enter a remark explaining the grievance');
      return;
    }

    const nextAssignee = getL1SupportAssignee();
    if (!nextAssignee) {
      alert('Error: No L1Support found for this store. Please contact administrator.');
      return;
    }

    setLoading(true);

    try {
      const actionData = availableActions.find(a => a.code === 'ESCALATE');

      const updateData = {
        id: row._id,
        req_data_entry: {
          action: actionData.action,
          sent_to_tag: actionData.sent_to_tag,
          text: remark,
          sent_by: {
            id: auth.id,
            name: getSingleuser[auth.id]?.name || {},
            office: user_current_office,
            designation: getSingleuser[auth.id]?.designation?.[user_current_office] || 'User'
          },
          sent_to: nextAssignee,
          time_stamp: new Date().toISOString()
        },
        curr_status: 'Open',
        curr_sent_by: auth.id,
        curr_sent_to: nextAssignee.id,
        curr_sent_to_string: `${nameStringFn(nextAssignee)}`.trim(),
        curr_sent_to_tag: actionData.sent_to_tag,
        opened: false,
        escalation_count: escalationCount + 1
      };

      await updateComplaint(updateData).unwrap();
      alert('Grievance raised successfully. Your complaint has been escalated to L1Support.');
      handleBack();
    } catch (error) {
      console.error('Error escalating complaint:', error);
      alert('Failed to escalate complaint. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!canEscalate) {
    let reason = '';
    if (row?.curr_status !== 'Closed') {
      reason = 'Escalation is only available for closed complaints.';
    } else if (!isWithinWindow) {
      reason = 'The 4-day escalation window has expired.';
    } else if (escalationCount >= maxEscalations) {
      reason = `Maximum escalation limit (${maxEscalations}) has been reached.`;
    }

    return (
      <FormContainer>
        <FormTitle>User Actions</FormTitle>
        <Alert severity="info" sx={{ marginBottom: '16px' }}>
          No actions available. {reason}
        </Alert>
        <ButtonsWrapper>
          <CancelButton onClick={handleBack}>Back</CancelButton>
        </ButtonsWrapper>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <FormTitle>Raise Grievance</FormTitle>
      
      <InfoBox style={{ background: '#fff3e0', border: '1px solid #ffb74d' }}>
        <strong>Escalation {escalationCount + 1} of {maxEscalations}</strong> -- 
        You have <strong>{daysRemaining} day(s)</strong> remaining to raise a grievance.
        {remainingEscalations <= 1 && (
          <span style={{ color: '#d32f2f' }}> This is your last available escalation.</span>
        )}
      </InfoBox>

      <FormRow>
        <FormField>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Describe your grievance"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder="Explain why the complaint has not been resolved satisfactorily..."
            variant="outlined"
          />
        </FormField>
      </FormRow>

      <ButtonsWrapper>
        <SubmitButton
          onClick={handleEscalate}
          disabled={loading || !remark.trim()}
        >
          {loading ? 'Submitting...' : 'Raise Grievance'}
        </SubmitButton>
        <CancelButton onClick={handleBack}>
          Cancel
        </CancelButton>
      </ButtonsWrapper>
    </FormContainer>
  );
};

export default UserComplaintForm;
